import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiKeys } from '../contexts/ApiKeysContext';
import {
  deleteProfile,
  getModerationProfiles,
  getModerationSettings,
  updateModerationSettings,
  updateProfileStatus
} from '../api/moderation';
import ThresholdModal from './ThresholdModal';
import WebhookModal from './WebhookModal';
import ImageSettingsModal from './ImageSettingsModal';
import ModerationQueueSection, { ModerationQueueTab } from './ModerationQueueSection';
import ModerationReviewPanel from './ModerationReviewPanel';
import { useQueueProfileSelection } from '../hooks/useQueueProfileSelection';
import type { ModerationProfileBase } from '../types/moderationProfile';
import './ModerationPage.css';

type Profile = ModerationProfileBase;

const ModerationPage: React.FC = () => {
  const { user } = useAuth();
  const { apiKeys, loading: apiKeysLoading, createApiKey, updateApiKeyTitle, deleteApiKey } = useApiKeys();

  const [pendingProfiles, setPendingProfiles] = useState<Profile[]>([]);
  const [rejectedProfiles, setRejectedProfiles] = useState<Profile[]>([]);
  const [approvedProfiles, setApprovedProfiles] = useState<Profile[]>([]);

  const [rejectedCursor, setRejectedCursor] = useState<string | null>(null);
  const [approvedCursor, setApprovedCursor] = useState<string | null>(null);
  const [rejectedHasMore, setRejectedHasMore] = useState(false);
  const [approvedHasMore, setApprovedHasMore] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [loadingApproved, setLoadingApproved] = useState(false);

  const [selectedApiKey, setSelectedApiKey] = useState<string | null>(null);
  const [thresholdSettings, setThresholdSettings] = useState<{
    auto_approve_threshold: number;
    auto_reject_threshold: number;
  } | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [imageSettings, setImageSettings] = useState<{
    global_configuration: { face_detect: boolean; ai_image_detect: boolean };
    per_image_configuration: Record<string, { face_detect?: boolean; ai_image_detect?: boolean }>;
    image_block: boolean;
  }>({
    global_configuration: { face_detect: true, ai_image_detect: true },
    per_image_configuration: {},
    image_block: false
  });

  const [activeTab, setActiveTab] = useState<'moderation' | 'config' | 'usage' | 'apiKeys'>('moderation');
  const [queueTab, setQueueTab] = useState<ModerationQueueTab>('pending');
  const { selectedProfileId, selectProfile } = useQueueProfileSelection(
    queueTab,
    pendingProfiles,
    approvedProfiles,
    rejectedProfiles
  );
  const [newApiKeyTitle, setNewApiKeyTitle] = useState('');
  const [newlyCreatedApiKey, setNewlyCreatedApiKey] = useState<string | null>(null);
  const [editingApiKeyId, setEditingApiKeyId] = useState<string | null>(null);
  const [editingApiKeyTitle, setEditingApiKeyTitle] = useState('');

  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [showImageSettingsModal, setShowImageSettingsModal] = useState(false);

  const pendingStatusUpdates = useRef<Map<string, { status: Profile['status']; previousStatus: Profile['status'] }>>(
    new Map()
  );

  useEffect(() => {
    if (!apiKeys.length) {
      setSelectedApiKey(null);
      return;
    }

    const savedApiKey = localStorage.getItem('moderation_selected_api_key');
    if (savedApiKey && apiKeys.some((key) => key.id === savedApiKey)) {
      setSelectedApiKey(savedApiKey);
      return;
    }

    setSelectedApiKey(apiKeys[0].id);
  }, [apiKeys]);

  useEffect(() => {
    if (!selectedApiKey) return;
    localStorage.setItem('moderation_selected_api_key', selectedApiKey);
    loadSettings(selectedApiKey);
    loadInitialProfiles(selectedApiKey);
  }, [selectedApiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const pendingUpdates = pendingStatusUpdates.current;
    return () => {
      pendingUpdates.clear();
    };
  }, []);

  const loadInitialProfiles = async (apiKeyId = selectedApiKey, forceRefresh = false) => {
    if (!user || !apiKeyId) return;

    setInitialLoading(true);
    try {
      const [pendingResult, rejectedResult, approvedResult] = await Promise.all([
        getModerationProfiles({ status: 'pending', api_key_id: apiKeyId, load_all_pending: true }),
        getModerationProfiles({ status: 'rejected', api_key_id: apiKeyId, limit: 40 }),
        getModerationProfiles({ status: 'approved', api_key_id: apiKeyId, limit: 40 })
      ]);

      if (pendingResult.success && pendingResult.profiles) setPendingProfiles(pendingResult.profiles);
      if (rejectedResult.success && rejectedResult.profiles) {
        setRejectedProfiles(rejectedResult.profiles);
        setRejectedCursor(rejectedResult.next_cursor || null);
        setRejectedHasMore(rejectedResult.has_more || false);
      }
      if (approvedResult.success && approvedResult.profiles) {
        setApprovedProfiles(approvedResult.profiles);
        setApprovedCursor(approvedResult.next_cursor || null);
        setApprovedHasMore(approvedResult.has_more || false);
      }
    } catch (error) {
      if (!forceRefresh) console.error('Failed to load moderation profiles:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadSettings = async (apiKeyId: string) => {
    try {
      const result = await getModerationSettings(apiKeyId);
      if (result.success && result.settings) {
        const thresholds = {
          auto_approve_threshold: result.settings.auto_approve_threshold,
          auto_reject_threshold: result.settings.auto_reject_threshold
        };
        setThresholdSettings(thresholds);

        if (result.settings.image_settings) {
          const settings = result.settings.image_settings as any;
          if (settings.face_detect !== undefined) {
            setImageSettings({
              global_configuration: {
                face_detect: settings.face_detect ?? true,
                ai_image_detect: settings.ai_image_detect ?? true
              },
              per_image_configuration: settings.per_image_configuration || {},
              image_block: settings.image_block ?? false
            });
          } else {
            setImageSettings({
              global_configuration: settings.global_configuration || { face_detect: true, ai_image_detect: true },
              per_image_configuration: settings.per_image_configuration || {},
              image_block: settings.image_block ?? false
            });
          }
        }
      }

      if (result.webhook_url !== undefined) setWebhookUrl(result.webhook_url);
      else setWebhookUrl(null);
    } catch (error) {
      console.error('Failed to load moderation settings:', error);
    }
  };

  const loadMoreRejected = async () => {
    if (!selectedApiKey || !rejectedCursor || loadingRejected) return;
    setLoadingRejected(true);
    try {
      const result = await getModerationProfiles({
        status: 'rejected',
        api_key_id: selectedApiKey,
        limit: 40,
        cursor: rejectedCursor
      });
      if (result.success && result.profiles) {
        setRejectedProfiles((prev) => [...prev, ...result.profiles]);
        setRejectedCursor(result.next_cursor || null);
        setRejectedHasMore(result.has_more || false);
      }
    } finally {
      setLoadingRejected(false);
    }
  };

  const loadMoreApproved = async () => {
    if (!selectedApiKey || !approvedCursor || loadingApproved) return;
    setLoadingApproved(true);
    try {
      const result = await getModerationProfiles({
        status: 'approved',
        api_key_id: selectedApiKey,
        limit: 40,
        cursor: approvedCursor
      });
      if (result.success && result.profiles) {
        setApprovedProfiles((prev) => [...prev, ...result.profiles]);
        setApprovedCursor(result.next_cursor || null);
        setApprovedHasMore(result.has_more || false);
      }
    } finally {
      setLoadingApproved(false);
    }
  };

  const handleStatusUpdate = async (profileId: string, newStatus: Profile['status']) => {
    const allProfiles = [...pendingProfiles, ...rejectedProfiles, ...approvedProfiles];
    const currentProfile = allProfiles.find((p) => p.id === profileId);
    if (!currentProfile) return;

    const previousStatus = currentProfile.status;
    pendingStatusUpdates.current.set(profileId, { status: newStatus, previousStatus });

    if (previousStatus === 'pending') setPendingProfiles((prev) => prev.filter((p) => p.id !== profileId));
    if (previousStatus === 'rejected') setRejectedProfiles((prev) => prev.filter((p) => p.id !== profileId));
    if (previousStatus === 'approved') setApprovedProfiles((prev) => prev.filter((p) => p.id !== profileId));

    const updated = { ...currentProfile, status: newStatus };
    if (newStatus === 'pending') setPendingProfiles((prev) => [updated, ...prev]);
    if (newStatus === 'rejected') setRejectedProfiles((prev) => [updated, ...prev]);
    if (newStatus === 'approved') setApprovedProfiles((prev) => [updated, ...prev]);

    try {
      const result = await updateProfileStatus(profileId, newStatus);
      if (!result.success) throw new Error('Status update failed');
      pendingStatusUpdates.current.delete(profileId);
    } catch (error) {
      pendingStatusUpdates.current.delete(profileId);
      await loadInitialProfiles(selectedApiKey || undefined, true);
      alert('Failed to update moderation status. Please try again.');
      console.error(error);
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    const confirmed = window.confirm(
      'Delete this profile and all associated stored media? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      const result = await deleteProfile(profileId);
      if (result.success) {
        setPendingProfiles((prev) => prev.filter((p) => p.id !== profileId));
        setRejectedProfiles((prev) => prev.filter((p) => p.id !== profileId));
        setApprovedProfiles((prev) => prev.filter((p) => p.id !== profileId));
      }
    } catch (error) {
      alert('Failed to delete profile.');
      console.error(error);
    }
  };

  const handleSaveThresholds = async (autoApprove: number, autoReject: number) => {
    if (!selectedApiKey) {
      alert('Select an API key first.');
      return;
    }
    const result = await updateModerationSettings(selectedApiKey, {
      auto_approve_threshold: autoApprove,
      auto_reject_threshold: autoReject
    });
    if (result.success) {
      setThresholdSettings({
        auto_approve_threshold: autoApprove,
        auto_reject_threshold: autoReject
      });
      setShowThresholdModal(false);
      await loadInitialProfiles(selectedApiKey, true);
    }
  };

  const handleSaveWebhook = async (url: string | null) => {
    if (!selectedApiKey) {
      alert('Select an API key first.');
      return;
    }
    const result = await updateModerationSettings(selectedApiKey, { webhook_url: url });
    if (result.success) {
      setWebhookUrl(result.webhook_url !== undefined ? result.webhook_url : url);
      setShowWebhookModal(false);
    }
  };

  const handleSaveImageSettings = async (
    globalConfig: { face_detect: boolean; ai_image_detect: boolean },
    perImageConfig: Record<string, { face_detect?: boolean; ai_image_detect?: boolean }>,
    imageBlock: boolean
  ) => {
    if (!selectedApiKey) {
      alert('Select an API key first.');
      return;
    }
    const result = await updateModerationSettings(selectedApiKey, {
      image_settings: {
        global_configuration: globalConfig,
        per_image_configuration: perImageConfig,
        image_block: imageBlock
      }
    });
    if (result.success) {
      setImageSettings({
        global_configuration: globalConfig,
        per_image_configuration: perImageConfig,
        image_block: imageBlock
      });
      setShowImageSettingsModal(false);
    }
  };

  const totalProfiles = pendingProfiles.length + rejectedProfiles.length + approvedProfiles.length;
  const averageScore = useMemo(() => {
    if (!totalProfiles) return 0;
    const all = [...pendingProfiles, ...rejectedProfiles, ...approvedProfiles];
    return Math.round(all.reduce((sum, profile) => sum + profile.trust_score, 0) / all.length);
  }, [approvedProfiles, pendingProfiles, rejectedProfiles, totalProfiles]);

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy API key:', error);
    }
  };

  const handleCreateApiKey = async () => {
    const title = newApiKeyTitle.trim() || 'Untitled Key';
    const result = await createApiKey(title);
    if (result?.apiKey) {
      setNewlyCreatedApiKey(result.apiKey);
      setNewApiKeyTitle('');
      setActiveTab('apiKeys');
    }
  };

  const handleDeleteApiKey = async (keyId: string) => {
    const confirmed = window.confirm('Delete this API key? This action cannot be undone.');
    if (!confirmed) return;
    await deleteApiKey(keyId);
  };

  const beginApiKeyEdit = (keyId: string, currentTitle: string) => {
    setEditingApiKeyId(keyId);
    setEditingApiKeyTitle(currentTitle);
  };

  const saveApiKeyTitle = async (keyId: string) => {
    const trimmed = editingApiKeyTitle.trim();
    if (!trimmed) return;
    await updateApiKeyTitle(keyId, trimmed);
    setEditingApiKeyId(null);
    setEditingApiKeyTitle('');
  };

  const renderReviewPanel = (
    profiles: Profile[],
    emptyMessage: string,
    options?: { loading?: boolean; footer?: React.ReactNode }
  ) => {
    if (options?.loading && profiles.length === 0) {
      return <div className="loading-state">Loading incoming users...</div>;
    }
    return (
      <ModerationReviewPanel
        profiles={profiles}
        emptyMessage={emptyMessage}
        selectedProfileId={selectedProfileId}
        onSelectProfile={selectProfile}
        onApprove={(id) => handleStatusUpdate(id, 'approved')}
        onReject={(id) => handleStatusUpdate(id, 'rejected')}
        onMoveToPending={(id) => handleStatusUpdate(id, 'pending')}
        onDelete={handleDeleteProfile}
        footer={options?.footer}
      />
    );
  };

  const renderModerationView = () => (
    <div className="tab-panel">
      <section className="stats-grid">
        <article className="stats-card">
          <p>Incoming queue</p>
          <h3>{pendingProfiles.length}</h3>
        </article>
        <article className="stats-card">
          <p>Total users</p>
          <h3>{totalProfiles}</h3>
        </article>
        <article className="stats-card">
          <p>Average trust score</p>
          <h3>{averageScore}</h3>
        </article>
        <article className="stats-card">
          <p>Webhook</p>
          <h3>{webhookUrl ? 'Connected' : 'Not configured'}</h3>
        </article>
      </section>

      <ModerationQueueSection
        queueTab={queueTab}
        onQueueTabChange={setQueueTab}
        pendingCount={pendingProfiles.length}
        approvedCount={approvedProfiles.length}
        rejectedCount={rejectedProfiles.length}
        pendingContent={renderReviewPanel(pendingProfiles, 'No incoming users right now.', {
          loading: initialLoading,
        })}
        approvedContent={renderReviewPanel(approvedProfiles, 'No approved users.', {
          footer: approvedHasMore ? (
            <button type="button" className="load-more-button" onClick={loadMoreApproved} disabled={loadingApproved}>
              {loadingApproved ? 'Loading...' : 'Load more'}
            </button>
          ) : undefined,
        })}
        rejectedContent={renderReviewPanel(rejectedProfiles, 'No rejected users.', {
          footer: rejectedHasMore ? (
            <button type="button" className="load-more-button" onClick={loadMoreRejected} disabled={loadingRejected}>
              {loadingRejected ? 'Loading...' : 'Load more'}
            </button>
          ) : undefined,
        })}
      />
    </div>
  );

  const renderConfigView = () => (
    <div className="tab-panel">
      <section className="config-grid">
        <article className="config-card">
          <p className="config-kicker">Threshold controls</p>
          <h2>Moderation confidence bands</h2>
          {thresholdSettings ? (
            <div className="threshold-readout">
              <div>
                <span>Approve automatically</span>
                <strong>&ge; {thresholdSettings.auto_approve_threshold}</strong>
              </div>
              <div>
                <span>Manual review</span>
                <strong>
                  {thresholdSettings.auto_reject_threshold} - {thresholdSettings.auto_approve_threshold - 1}
                </strong>
              </div>
              <div>
                <span>Reject automatically</span>
                <strong>&lt; {thresholdSettings.auto_reject_threshold}</strong>
              </div>
            </div>
          ) : (
            <p className="subtext">Threshold settings are loading.</p>
          )}
          <div className="config-actions">
            <button className="pill-button primary" onClick={() => setShowThresholdModal(true)} disabled={!selectedApiKey || !thresholdSettings}>
              Open threshold controls
            </button>
            <button className="pill-button" onClick={() => loadSettings(selectedApiKey || '')} disabled={!selectedApiKey}>
              Reload settings
            </button>
          </div>
        </article>

        <article className="config-card">
          <p className="config-kicker">Image policy</p>
          <h2>Optional image verification rules</h2>
          <p className="subtext">
            Configure face detection and AI-image checks per uploaded image. These rules still use the current moderation
            settings contract.
          </p>
          <div className="config-actions">
            <button className="pill-button" onClick={() => setShowImageSettingsModal(true)} disabled={!selectedApiKey}>
              Edit image policy
            </button>
          </div>
        </article>
      </section>
    </div>
  );

  const renderUsageView = () => (
    <div className="tab-panel">
      <section className="usage-grid">
        <article className="usage-card">
          <p className="config-kicker">Webhooks</p>
          <h2>Delivery and integration docs</h2>
          <p className="subtext">
            Send moderation decisions to your backend. Configure an endpoint URL, then process events when profiles are
            approved, rejected, or returned to pending.
          </p>
          <pre className="docs-snippet">
{`POST /webhook-endpoint
{
  "profile_id": "string",
  "status": "approved | rejected | pending",
  "trust_score": 0
}`}
          </pre>
          <p className="subtext">Current endpoint: {webhookUrl || 'Not configured'}</p>
          <div className="config-actions">
            <button className="pill-button primary" onClick={() => setShowWebhookModal(true)} disabled={!selectedApiKey}>
              Configure webhook URL
            </button>
          </div>
        </article>
      </section>
    </div>
  );

  const renderApiKeysView = () => (
    <div className="tab-panel">
      <section className="api-keys-grid">
        <article className="config-card">
          <p className="config-kicker">Create key</p>
          <h2>Generate a new API key</h2>
          <p className="subtext">Create keys for moderation ingestion and secure backend calls.</p>
          <div className="api-key-create-row">
            <input
              className="api-key-title-input"
              type="text"
              placeholder="Key title (optional)"
              value={newApiKeyTitle}
              onChange={(e) => setNewApiKeyTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateApiKey();
              }}
            />
            <button className="pill-button primary" onClick={handleCreateApiKey} disabled={apiKeysLoading}>
              {apiKeysLoading ? 'Creating...' : 'Create key'}
            </button>
          </div>
          {!!newlyCreatedApiKey && (
            <div className="new-api-key-box">
              <p>Copy this key now. It will only be shown once.</p>
              <code>{newlyCreatedApiKey}</code>
              <div className="config-actions">
                <button className="pill-button" onClick={() => copyText(newlyCreatedApiKey)}>Copy key</button>
                <button className="pill-button" onClick={() => setNewlyCreatedApiKey(null)}>Dismiss</button>
              </div>
            </div>
          )}
        </article>

        <article className="config-card">
          <p className="config-kicker">Manage keys</p>
          <h2>Active API keys</h2>
          {apiKeysLoading && apiKeys.length === 0 ? (
            <div className="loading-state">Loading API keys...</div>
          ) : apiKeys.length === 0 ? (
            <div className="empty-state">No API keys yet.</div>
          ) : (
            <div className="api-key-list">
              {apiKeys.map((key) => (
                <div key={key.id} className="api-key-list-item">
                  <div className="api-key-list-main">
                    {editingApiKeyId === key.id ? (
                      <div className="api-key-edit-row">
                        <input
                          className="api-key-title-input"
                          type="text"
                          value={editingApiKeyTitle}
                          onChange={(e) => setEditingApiKeyTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveApiKeyTitle(key.id);
                            if (e.key === 'Escape') {
                              setEditingApiKeyId(null);
                              setEditingApiKeyTitle('');
                            }
                          }}
                        />
                        <button className="pill-button" onClick={() => saveApiKeyTitle(key.id)} disabled={!editingApiKeyTitle.trim()}>
                          Save
                        </button>
                      </div>
                    ) : (
                      <>
                        <strong>{key.title || 'Untitled Key'}</strong>
                        <p>{key.key_prefix}</p>
                      </>
                    )}
                  </div>
                  <div className="api-key-actions">
                    {editingApiKeyId !== key.id && (
                      <button className="pill-button" onClick={() => beginApiKeyEdit(key.id, key.title || 'Untitled Key')}>
                        Rename
                      </button>
                    )}
                    <button className="pill-button" onClick={() => handleDeleteApiKey(key.id)} disabled={apiKeysLoading}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>
    </div>
  );

  return (
    <div className="moderation-dashboard">
      <section className="dashboard-shell">
        <aside className="dashboard-nav" aria-label="Dashboard navigation">
          <div className="dashboard-nav-brand">
            <p className="nav-kicker">Dashboard</p>
            <h1 className="nav-title">Moderation</h1>
          </div>
          <nav className="dashboard-nav-links">
            <button
              type="button"
              className={`nav-tab ${activeTab === 'moderation' ? 'active' : ''}`}
              onClick={() => setActiveTab('moderation')}
            >
              Moderation
            </button>
            <button
              type="button"
              className={`nav-tab ${activeTab === 'config' ? 'active' : ''}`}
              onClick={() => setActiveTab('config')}
            >
              Config
            </button>
            <button
              type="button"
              className={`nav-tab ${activeTab === 'usage' ? 'active' : ''}`}
              onClick={() => setActiveTab('usage')}
            >
              Usage
            </button>
            <button
              type="button"
              className={`nav-tab ${activeTab === 'apiKeys' ? 'active' : ''}`}
              onClick={() => setActiveTab('apiKeys')}
            >
              API Key
            </button>
          </nav>
        </aside>

        <main className="dashboard-main">
          <section className="dashboard-head">
            <div>
              <p className="head-kicker">Trust operations</p>
              <h2>Review users and configure moderation behavior</h2>
            </div>
            <div className="head-actions">
              <select
                className="api-key-selector"
                value={selectedApiKey || ''}
                onChange={(e) => setSelectedApiKey(e.target.value || null)}
              >
                <option value="" disabled={apiKeys.length > 0}>
                  {apiKeys.length ? 'Select API key' : 'No API keys'}
                </option>
                {apiKeys.map((key) => (
                  <option key={key.id} value={key.id}>
                    {key.title || key.key_prefix}
                  </option>
                ))}
              </select>
              <button className="pill-button" onClick={() => loadInitialProfiles(selectedApiKey || undefined, true)}>
                {initialLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </section>

          {!apiKeys.length && (
            <section className="info-banner">
              <p>Create an API key before sending user profiles into moderation.</p>
              <button className="pill-button" onClick={() => setActiveTab('apiKeys')}>
                Open API key tab
              </button>
            </section>
          )}

          {activeTab === 'moderation' && renderModerationView()}
          {activeTab === 'config' && renderConfigView()}
          {activeTab === 'usage' && renderUsageView()}
          {activeTab === 'apiKeys' && renderApiKeysView()}
        </main>
      </section>

      {showThresholdModal && thresholdSettings && selectedApiKey && (
        <ThresholdModal
          autoApproveThreshold={thresholdSettings.auto_approve_threshold}
          autoRejectThreshold={thresholdSettings.auto_reject_threshold}
          onSave={handleSaveThresholds}
          onClose={() => setShowThresholdModal(false)}
        />
      )}
      {showWebhookModal && selectedApiKey && (
        <WebhookModal
          webhookUrl={webhookUrl || ''}
          onSave={handleSaveWebhook}
          onClose={() => setShowWebhookModal(false)}
        />
      )}
      {showImageSettingsModal && selectedApiKey && (
        <ImageSettingsModal
          globalConfig={imageSettings.global_configuration}
          perImageConfig={imageSettings.per_image_configuration}
          imageBlock={imageSettings.image_block}
          onSave={handleSaveImageSettings}
          onClose={() => setShowImageSettingsModal(false)}
        />
      )}

    </div>
  );
};

export default ModerationPage;
